import { useMemo, forwardRef } from 'react';
import { MoreHorizontal } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAzureProfilePhoto } from '@/entities/auth';
import { User } from '@/entities/layout/model/types';
import { cn } from '@/lib/utils';

interface SidebarFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  user?: User;
  isCollapsed: boolean;
}

export const MenuTrigger = forwardRef<HTMLDivElement, SidebarFooterProps>(({ user, isCollapsed, ...props }, ref) => {
  const azurePhotoUrl = useAzureProfilePhoto();
  const initials = useMemo(
    () =>
      user?.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2),
    [user?.name]
  );

  if (!user) return null;

  return (
    <div className="p-3 border-t mt-auto" ref={ref} {...props}>
      <div
        className={cn(
          'flex items-center gap-3 rounded-md p-2 hover:bg-accent cursor-pointer transition-colors',
          isCollapsed && 'justify-center'
        )}>
        <Avatar className="w-8 h-8 rounded-lg">
          <AvatarImage src={user.picture || azurePhotoUrl || undefined} alt={user.name} />
          <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">{initials}</AvatarFallback>
        </Avatar>
        {!isCollapsed && (
          <>
            <div className="flex-1 overflow-hidden">
              <div className="font-semibold text-sm truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
          </>
        )}
      </div>
    </div>
  );
});

MenuTrigger.displayName = 'MenuTrigger';
